// Квадратная обрезка фото перед загрузкой: фото двигают пальцем или мышью
// внутри квадратной рамки и масштабируют ползунком или колесом, на выходе —
// квадратный JPEG. Без библиотеки: сборка сайта — один iife-файл на все
// страницы, и кроппер весом в десятки килобайт грузился бы везде ради одного
// поля.
//
// Разметка окна — в ui/_upload_file.slim (crop: true): <dialog> с
// [data-crop-frame] > img, [data-crop-zoom], [data-crop-cancel], [data-crop-save].
// Рамка фокусируемая (tabindex="0"): фото в ней двигают и стрелками.

const JPEG_QUALITY = 0.92
// Во сколько раз можно приблизить относительно «фото закрывает рамку».
const MAX_ZOOM = 4
// Чувствительность колеса и жеста на тачпаде.
const WHEEL_ZOOM_SPEED = 0.002
// Сдвиг фото стрелками в px рамки; с Shift — крупным шагом.
const KEY_STEP = 10
const KEY_STEP_LARGE = 50
const KEY_DIRECTIONS = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1]
}

// Масштаб zoom — относительно «фото закрывает рамку» (1). Меньше 1 — фото
// уменьшается вплоть до «влезает целиком», с белыми полями по краям: так
// голову у края вертикального снимка можно поставить в центр, и ничего не
// обрезается. Ползунок ходит по логарифму масштаба — шаг ощущается одинаково
// и при уменьшении, и при приближении.
//
// minSide — меньшая сторона, которую примет сервер (CoachMainPhotoUploader::
// MIN_SIDE): кадр, вырезанный из достаточно большого фото, но меньше её
// (сильно приблизили), растягиваем до неё, а не отдаём серверу на отказ.
// Мелкий исходник окно не открывает: файл уходит как есть, и сервер
// отклоняет его по настоящему размеру. Иначе белые поля уменьшенного фото
// дотягивали бы квадрат до minSide, и 500×1000 прошло бы проверку.
// maxSide — больше версии :xlarge (1200) на сервер отправлять незачем.
export function cropToSquare(
  dialog,
  file,
  { minSide = 600, maxSide = 1200 } = {}
) {
  const frame = dialog.querySelector('[data-crop-frame]')
  const image = frame.querySelector('img')
  const zoomInput = dialog.querySelector('[data-crop-zoom]')
  const saveButton = dialog.querySelector('[data-crop-save]')
  const cancelButton = dialog.querySelector('[data-crop-cancel]')
  const url = URL.createObjectURL(file)

  return new Promise((resolve) => {
    let natural = { width: 0, height: 0 }
    let frameSize = 0
    let coverScale = 1
    let zoom = 1
    let minZoom = 1
    let maxZoom = MAX_ZOOM
    let offset = { x: 0, y: 0 }
    let drag = null

    const scale = () => coverScale * zoom

    // В центре рамки может оказаться любая точка фото, но не пустота за его
    // краем: голову у самого края снимка можно поставить в центр (над ней
    // будет белое поле), а потерять фото из рамки целиком — нельзя.
    const clampAxis = (value, size) =>
      Math.min(frameSize / 2, Math.max(frameSize / 2 - size, value))

    const render = () => {
      const width = natural.width * scale()
      const height = natural.height * scale()
      offset = { x: clampAxis(offset.x, width), y: clampAxis(offset.y, height) }
      image.style.width = `${width}px`
      image.style.height = `${height}px`
      image.style.transform = `translate(${offset.x}px, ${offset.y}px)`
    }

    // Масштаб меняется вокруг центра рамки: точка фото, которая была в центре,
    // в нём и остаётся — лицо, выставленное по центру, не уезжает при зуме.
    const setZoom = (nextZoom) => {
      const center = frameSize / 2
      const pointX = (center - offset.x) / scale()
      const pointY = (center - offset.y) / scale()
      zoom = Math.min(maxZoom, Math.max(minZoom, nextZoom))
      offset = { x: center - pointX * scale(), y: center - pointY * scale() }
      zoomInput.value = String(Math.log(zoom))
      render()
    }

    const onPointerDown = (event) => {
      drag = { x: event.clientX, y: event.clientY, offset: { ...offset } }
      frame.setPointerCapture(event.pointerId)
    }
    const onPointerMove = (event) => {
      if (!drag) return
      offset = {
        x: drag.offset.x + event.clientX - drag.x,
        y: drag.offset.y + event.clientY - drag.y
      }
      render()
    }
    const onPointerUp = () => {
      drag = null
    }
    // С клавиатуры фото двигают стрелками по сфокусированной рамке — тот же
    // сдвиг offset, что и перетаскивание, без указателя.
    const onKeyDown = (event) => {
      const direction = KEY_DIRECTIONS[event.key]
      if (!direction) return

      event.preventDefault()
      const step = event.shiftKey ? KEY_STEP_LARGE : KEY_STEP
      offset = {
        x: offset.x + direction[0] * step,
        y: offset.y + direction[1] * step
      }
      render()
    }
    const onSlider = () => setZoom(Math.exp(Number(zoomInput.value)))
    const onWheel = (event) => {
      event.preventDefault()
      setZoom(zoom * Math.exp(-event.deltaY * WHEEL_ZOOM_SPEED))
    }

    const finish = (result) => {
      frame.removeEventListener('pointerdown', onPointerDown)
      frame.removeEventListener('pointermove', onPointerMove)
      frame.removeEventListener('pointerup', onPointerUp)
      frame.removeEventListener('pointercancel', onPointerUp)
      frame.removeEventListener('wheel', onWheel)
      frame.removeEventListener('keydown', onKeyDown)
      zoomInput.removeEventListener('input', onSlider)
      saveButton.removeEventListener('click', onSave)
      cancelButton.removeEventListener('click', onCancel)
      dialog.removeEventListener('cancel', onCancel)
      if (dialog.open) dialog.close()
      URL.revokeObjectURL(url)
      image.removeAttribute('src')
      resolve(result)
    }

    const onCancel = (event) => {
      event?.preventDefault()
      finish(null)
    }

    const onSave = () => {
      // Сколько пикселей исходника помещается в рамку по стороне.
      const sourceSize = frameSize / scale()
      const outputSize = Math.round(
        Math.min(maxSide, Math.max(minSide, sourceSize))
      )
      const ratio = outputSize / frameSize
      const canvas = document.createElement('canvas')
      canvas.width = outputSize
      canvas.height = outputSize
      const context = canvas.getContext('2d')
      // Белые поля у уменьшенного фото, и прозрачный PNG в JPEG без подложки
      // стал бы чёрным.
      context.fillStyle = '#fff'
      context.fillRect(0, 0, outputSize, outputSize)
      context.imageSmoothingQuality = 'high'
      context.drawImage(
        image,
        offset.x * ratio,
        offset.y * ratio,
        natural.width * scale() * ratio,
        natural.height * scale() * ratio
      )

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            finish(null)
            return
          }

          const name = `${file.name.replace(/\.[^.]+$/, '') || 'photo'}.jpg`
          finish(
            new File([blob], name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            })
          )
        },
        'image/jpeg',
        JPEG_QUALITY
      )
    }

    // Окно открываем только после загрузки фото: размеры исходника нужны
    // до показа (мелкий — без окна), а Save до загрузки рисовал бы пустоту.
    image.onload = () => {
      natural = { width: image.naturalWidth, height: image.naturalHeight }
      const shorter = Math.min(natural.width, natural.height)
      const longer = Math.max(natural.width, natural.height)
      if (shorter < minSide) {
        finish(file)
        return
      }

      // Размер рамки известен только у открытого окна.
      dialog.showModal()
      frameSize = frame.clientWidth
      coverScale = frameSize / shorter
      minZoom = shorter / longer
      maxZoom = MAX_ZOOM
      zoom = 1
      offset = {
        x: (frameSize - natural.width * scale()) / 2,
        y: (frameSize - natural.height * scale()) / 2
      }

      zoomInput.min = String(Math.log(minZoom))
      zoomInput.max = String(Math.log(maxZoom))
      zoomInput.step = 'any'
      zoomInput.value = '0'
      render()
    }
    image.onerror = () => finish(file)

    frame.addEventListener('pointerdown', onPointerDown)
    frame.addEventListener('pointermove', onPointerMove)
    frame.addEventListener('pointerup', onPointerUp)
    frame.addEventListener('pointercancel', onPointerUp)
    frame.addEventListener('wheel', onWheel, { passive: false })
    frame.addEventListener('keydown', onKeyDown)
    zoomInput.addEventListener('input', onSlider)
    saveButton.addEventListener('click', onSave)
    cancelButton.addEventListener('click', onCancel)
    dialog.addEventListener('cancel', onCancel)

    image.src = url
  })
}
